<template>
  <section
    v-if="showSection"
    class="action-bar__additional-content action-bar-cross-sell"
    :data-testid="testId"
  >
    <div class="action-bar-cross-sell__header">
      <div class="action-bar-cross-sell__title-row">
        <h3 class="action-bar__additional-content-title">
          <slot name="title" :title="resolvedTitle">
            {{ resolvedTitle }}
          </slot>
        </h3>
        <DictionaryInfoIcon
          v-if="hasHighlightedCards"
          dictionary-key="actionBar.crossSell.simulationProducts.info"
          fallback="The highlighted products are used in the camera image simulation."
          data-testid="action-bar-cross-sell-simulation-info"
        />
      </div>
      <p v-if="subtitle" class="action-bar-cross-sell__subtitle">
        {{ subtitle }}
      </p>
    </div>

    <div
      v-if="loading"
      class="action-bar-cross-sell__skeleton"
      aria-busy="true"
    >
      <LoadingSkeleton
        v-for="n in 3"
        :key="n"
        class="action-bar-cross-sell__skeleton-card"
      />
    </div>

    <template v-else-if="visibleCards.length > 0">
      <div class="action-bar-cross-sell__grid">
        <ActionBarCrossSellCard
          v-for="(card, index) in primaryCards"
          :key="card.groupId"
          :card="card"
          :disabled="busy"
          :overlay-append-to="overlayAppendTo"
          :test-id="
            cardTestIdPrefix ? `${cardTestIdPrefix}-${card.groupId}` : undefined
          "
          @add="(sku, groupId) => $emit('add', sku, groupId)"
          @update:selected-sku="
            (sku, groupId) =>
              $emit('update:selectedSku', sku, groupId, index + 1)
          "
        >
          <template v-if="$slots.action" #action="actionProps">
            <slot name="action" v-bind="actionProps" />
          </template>
        </ActionBarCrossSellCard>
      </div>

      <Button
        v-if="showMoreLink"
        type="button"
        style-type="link"
        size="small"
        class="action-bar-cross-sell__more"
        data-testid="action-bar-cross-sell-more"
        :disabled="busy"
        @click="onShowMore"
      >
        {{ moreLinkText }}
      </Button>

      <div
        v-if="expanded && secondaryCards.length > 0"
        class="action-bar-cross-sell__grid action-bar-cross-sell__grid--secondary"
      >
        <ActionBarCrossSellCard
          v-for="(card, index) in secondaryCards"
          :key="card.groupId"
          :card="card"
          :disabled="busy"
          :overlay-append-to="overlayAppendTo"
          :test-id="
            cardTestIdPrefix ? `${cardTestIdPrefix}-${card.groupId}` : undefined
          "
          @add="(sku, groupId) => $emit('add', sku, groupId)"
          @update:selected-sku="
            (sku, groupId) =>
              $emit(
                'update:selectedSku',
                sku,
                groupId,
                primaryCards.length + index + 1
              )
          "
        >
          <template v-if="$slots.action" #action="actionProps">
            <slot name="action" v-bind="actionProps" />
          </template>
        </ActionBarCrossSellCard>
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import { LoadingSkeleton, Button } from '@hypercodestudio/basler-components';
import ActionBarCrossSellCard from './ActionBarCrossSellCard.vue';
import DictionaryInfoIcon from '~/components/global/DictionaryInfoIcon.vue';
import type { ActionBarCrossSellCardModel } from '~/utils/shop/buildActionBarCrossSellCards';
import { sortActionBarCrossSellVariantsByLength } from '~/utils/shop/sortActionBarCrossSellVariantsByLength';

const props = withDefaults(
  defineProps<{
    cards: ActionBarCrossSellCardModel[];
    productName?: string;
    title?: string;
    subtitle?: string;
    testId?: string;
    cardTestIdPrefix?: string;
    loading?: boolean;
    busy?: boolean;
    overlayAppendTo?: HTMLElement | 'body' | 'self';
  }>(),
  {
    productName: '',
    title: '',
    subtitle: undefined,
    testId: 'action-bar-cross-sell',
    cardTestIdPrefix: '',
    loading: false,
    busy: false,
    overlayAppendTo: undefined
  }
);

const emit = defineEmits<{
  add: [sku: string, groupId: string];
  'update:selectedSku': [sku: string, groupId: string, position: number];
  moreProductsClick: [remainingCount: number];
}>();

const { $textDictionary } = useNuxtApp();
const expanded = ref(false);

/** Display-only: numeric length order; selectedVariantSku stays as selected. */
const visibleCards = computed(() =>
  props.cards.map((card) => ({
    ...card,
    variants: sortActionBarCrossSellVariantsByLength(card.variants)
  }))
);
const hasHighlightedCards = computed(() =>
  visibleCards.value.some((card) => card.highlighted)
);
const primaryCards = computed(() => visibleCards.value.slice(0, 3));
const secondaryCards = computed(() => visibleCards.value.slice(3));
const showMoreLink = computed(
  () => !expanded.value && secondaryCards.value.length > 0
);

function onShowMore(): void {
  emit('moreProductsClick', secondaryCards.value.length);
  expanded.value = true;
}

const showSection = computed(
  () => props.loading || visibleCards.value.length > 0
);

const resolvedTitle = computed(
  () =>
    props.title ||
    $textDictionary.value['actionBar.crossSell.title'] ||
    'Recommended for commissioning'
);

const subtitle = computed(() => {
  if (props.subtitle !== undefined) {
    return props.subtitle;
  }
  if (!props.productName) {
    return '';
  }
  const template =
    $textDictionary.value['actionBar.crossSell.subtitle'] ??
    'Frequently bought together with your {productName}';
  return template.replace('{productName}', props.productName);
});

const moreLinkText = computed(() => {
  const count = secondaryCards.value.length;
  if (count === 1) {
    return (
      $textDictionary.value['actionBar.crossSell.showMoreSingular']?.replace(
        '{count}',
        '1'
      ) ?? 'Show 1 more product'
    );
  }
  return (
    $textDictionary.value['actionBar.crossSell.showMorePlural']?.replace(
      '{count}',
      String(count)
    ) ?? `Show ${count} more products`
  );
});

watch(
  () => props.cards.map((card) => card.groupId).join(','),
  () => {
    expanded.value = false;
  }
);
</script>

<style scoped>
.action-bar-cross-sell {
  width: 100%;
  margin-block-start: 1.5rem;
}

.action-bar-cross-sell__header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem 1.5rem;
  margin-block-end: 0.75rem;
  h3 {
    margin-block-end: 0;
  }
}

.action-bar-cross-sell__title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-bar-cross-sell__subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: var(--basler-color-grey-60, #222);
}

.action-bar-cross-sell__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.action-bar-cross-sell__grid--secondary {
  margin-block-start: 1rem;
  animation: action-bar-cross-sell-fade-in 0.25s ease;
}

.action-bar-cross-sell__more {
  display: flex;
  justify-content: center;
  margin: 1rem auto 0;
}

.action-bar-cross-sell__skeleton {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.action-bar-cross-sell__skeleton-card {
  width: 100%;
  height: 5.375rem;
}

@keyframes action-bar-cross-sell-fade-in {
  from {
    opacity: 0;
    transform: translateY(0.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 991px) {
  .action-bar-cross-sell__grid,
  .action-bar-cross-sell__skeleton {
    grid-template-columns: 1fr;
  }
}
</style>
